import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useGetOptionSetsByNameQuery } from '@/app/services/lookups';
import { addToast } from '@/app/slices/toast';
import { AccountAutoComplete } from '@/components';
import { StorageLocationAutoComplete } from '@/components';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { OptionSetsDropdown } from '@/components/common/option-sets-dropdown';
import { StorageLocationCombobox } from '@/components/common/storage-location-combobox';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button as UiButton } from '@/components/ui/button';
import {
  DATE_PICKER_COMPACT,
  DatePickerInput,
} from '@/components/ui/date-picker-input';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Semen } from '@/rtk-query.gql';
import {
  useAddSemenTransaction,
  useAddSemenTransactionV2,
} from '@/services/semen-service/hooks';
import { SemenDto } from '@/services/semen-service/types';

import {
  newSemenTransactionFormSchema,
  NewSemenTransactionFormValues,
} from './schema';

type INewSemenTransactionFormProps = {
  semenId: string;
  onSuccess?: (data: NewSemenTransactionFormValues) => void;
  onError?: (error: unknown) => void;
  onCancel?: () => void;
  semen?: Semen | SemenDto;
  semens?: (Semen | SemenDto)[];
  calendarContainer?: HTMLElement;
};

export type INewSemenTransactionFormRef = {
  submit: () => void;
};

export const NewSemenTransactionForm = forwardRef<
  INewSemenTransactionFormRef,
  INewSemenTransactionFormProps
>(
  (
    { semenId, onSuccess, onError, onCancel, semen, semens, calendarContainer },
    ref,
  ) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isBaseUi = useFeatureFlag('new_semen_transaction_prime_to_base_ui');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { mutateAsync: addSemenTransaction } = useAddSemenTransaction();
    const { mutateAsync: addSemenTransactionV2 } = useAddSemenTransactionV2();
    const { data: optionSets = [] } =
      useGetOptionSetsByNameQuery('Semen Transaction');

    const {
      handleSubmit,
      control,
      reset,
      setValue,
      setError,
      watch,
      formState: { errors },
    } = useForm<NewSemenTransactionFormValues>({
      resolver: zodResolver(newSemenTransactionFormSchema),
      defaultValues: {
        date: new Date(),
        transactionId: '',
        quantity: undefined,
        description: '',
        newOwner: undefined,
        newLocation: undefined,
        semenId: semenId,
        previousOwner: undefined,
        quantityBeforeTransaction: undefined,
        previousLocation: undefined,
      },
    });

    const transactionId = watch('transactionId');
    const transactionLabel = useMemo(() => {
      const option = optionSets.find((opt) => opt.id === transactionId);
      return option?.label || '';
    }, [transactionId, optionSets]);

    useEffect(() => {
      if (transactionLabel && transactionLabel !== 'Transfer') {
        setValue('newOwner', undefined);
        setValue('newLocation', undefined);
      }
    }, [transactionLabel, setValue]);

    useEffect(() => {
      if (!semen) return;

      const currentValues = watch();
      const updates: Partial<NewSemenTransactionFormValues> = {};

      const isSemen = 'ownerId' in semen && 'ownerName' in semen;

      if (isSemen) {
        const semenDto = semen as SemenDto;

        if (semenDto.ownerId && semenDto.ownerName) {
          updates.previousOwner = {
            id: semenDto.ownerId,
            name: semenDto.ownerName,
          };
        }

        if (semenDto.storageLocation?.id && semenDto.storageLocation?.name) {
          updates.previousLocation = {
            id: semenDto.storageLocation.id,
            name: semenDto.storageLocation.name,
          };
        }

        if ('quantity' in semenDto && semenDto.quantity != null) {
          const quantity =
            typeof semenDto.quantity === 'string'
              ? parseInt(semenDto.quantity, 10)
              : typeof semenDto.quantity === 'number'
                ? semenDto.quantity
                : undefined;
          if (quantity !== undefined && !Number.isNaN(quantity)) {
            updates.quantityBeforeTransaction = quantity;
          }
        }
      } else {
        const semenType = semen as Semen;

        if (semenType.owner?.id && semenType.owner?.name) {
          updates.previousOwner = {
            id: semenType.owner.id,
            name: semenType.owner.name,
          };
        }

        if (semenType.storageLocation?.id && semenType.storageLocation?.name) {
          updates.previousLocation = {
            id: semenType.storageLocation.id,
            name: semenType.storageLocation.name,
          };
        }

        if ('quantity' in semenType && semenType.quantity != null) {
          const quantity =
            typeof semenType.quantity === 'string'
              ? parseInt(semenType.quantity, 10)
              : typeof semenType.quantity === 'number'
                ? semenType.quantity
                : undefined;
          if (quantity !== undefined && !Number.isNaN(quantity)) {
            updates.quantityBeforeTransaction = quantity;
          }
        }
      }

      if (Object.keys(updates).length > 0) {
        reset({
          ...currentValues,
          ...updates,
        });
      }
    }, [semen, reset, watch]);

    const getFormErrorMessage = (name: keyof NewSemenTransactionFormValues) => {
      return (
        errors[name] && (
          <small className="mt-1 text-sm text-red-500">
            {errors[name]?.message}
          </small>
        )
      );
    };

    const isBulkMode = (semens?.length ?? 0) > 1;

    const onSubmit = async (data: NewSemenTransactionFormValues) => {
      if (isSubmitting) return;

      if (data.quantity == null) {
        setError('quantity', { message: 'Quantity is required' });
        return;
      }

      try {
        setIsSubmitting(true);

        const input = {
          semenId: semenId || '',
          ...data,
          date: data.date?.toISOString(),
          newOwnerId: data.newOwner?.id || undefined,
          storageLocationId: data.newLocation?.id || undefined,
          previousStorageLocationId: data.previousLocation?.id || undefined,
          previousOwnerId: data.previousOwner?.id || undefined,
          sireId:
            semen?.sire?.id ||
            ('sireId' in (semen || {})
              ? (semen as SemenDto)?.sireId
              : undefined),
        };
        const { previousLocation, newOwner, newLocation, ...rest } = input;

        await addSemenTransaction(rest);

        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('semenTransaction.transactionCreatedSuccessfully'),
            life: ToastLife.MEDIUM,
          }),
        );

        onSuccess?.(data);
        reset();
        onCancel?.();
      } catch (error) {
        console.error('Failed to create transaction:', error);
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error'),
            detail: t('semenTransaction.failedToCreateTransaction'),
            life: ToastLife.MEDIUM,
          }),
        );
        onError?.(error);
      } finally {
        setIsSubmitting(false);
      }
    };

    const bulkSubmit = async (data: NewSemenTransactionFormValues) => {
      if (isSubmitting) return;

      try {
        setIsSubmitting(true);

        const transactions = (semens || []).map((s) => {
          const isSemenDto = 'ownerId' in s && 'ownerName' in s;
          const rawNumberToAdd = isSemenDto
            ? (s as SemenDto).numberOfStrawsToAdd
            : undefined;
          const numberOfStrawsToAdd =
            rawNumberToAdd != null ? Number(rawNumberToAdd) : Number.NaN;

          const quantity =
            !Number.isNaN(numberOfStrawsToAdd) && numberOfStrawsToAdd > 0
              ? numberOfStrawsToAdd
              : (s.quantity as number);

          return {
            semenId: s.id || '',
            date: data.date?.toISOString(),
            transactionId: data.transactionId,
            quantity: quantity,
            description: data.description,
            newOwnerId: data.newOwner?.id || undefined,
            storageLocationId: data.newLocation?.id || undefined,
            previousStorageLocationId: data?.previousLocation?.id || undefined,
            previousOwnerId: data?.previousOwner?.id || undefined,
          };
        });

        await addSemenTransactionV2({ transactions });

        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('semenTransaction.transactionCreatedSuccessfully'),
            life: ToastLife.MEDIUM,
          }),
        );

        onSuccess?.(data);
        reset();
        onCancel?.();
      } catch (error) {
        console.error('Failed to create transactions:', error);
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error'),
            detail: t('semenTransaction.failedToCreateTransaction'),
            life: ToastLife.MEDIUM,
          }),
        );
        onError?.(error);
      } finally {
        setIsSubmitting(false);
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        if (isBulkMode) {
          handleSubmit(bulkSubmit)();
        } else {
          handleSubmit(onSubmit)();
        }
      },
    }));

    if (isBaseUi) {
      const allowedTransactionLabels = ['Shipment', 'Transfer', 'Adjustment'];
      const transactionOptions = optionSets
        .filter((item) => allowedTransactionLabels.includes(item.label || ''))
        .map((item) => ({ value: item.id, label: item.label || '' }));
      const isTransfer = transactionLabel === 'Transfer';

      const renderField = ({
        name,
        label,
        required,
        children,
      }: {
        name: keyof NewSemenTransactionFormValues;
        label: string;
        required?: boolean;
        children: React.ReactNode;
      }) => (
        <Field data-invalid={!!errors[name]}>
          <FieldLabel className="font-normal" htmlFor={name}>
            {label}
            {required && <span className="text-destructive">*</span>}
          </FieldLabel>
          {children}
          <FieldError
            errors={[errors[name] as { message?: string } | undefined]}
          />
        </Field>
      );

      const renderAccountField = (
        name: 'newOwner' | 'previousOwner',
        label: string,
        disabled: boolean,
      ) =>
        renderField({
          name,
          label,
          children: (
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <AccountsCombobox
                  className="w-full"
                  container={calendarContainer}
                  value={field.value?.id}
                  disabled={disabled}
                  onChange={(accountId) =>
                    field.onChange(
                      accountId ? { id: accountId, name: '' } : undefined,
                    )
                  }
                />
              )}
            />
          ),
        });

      const renderLocationField = (
        name: 'newLocation' | 'previousLocation',
        label: string,
        disabled: boolean,
      ) =>
        renderField({
          name,
          label,
          children: (
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <StorageLocationCombobox
                  className="w-full"
                  value={field.value ?? null}
                  onChange={(location) =>
                    field.onChange(
                      location
                        ? { id: location.id, name: location.name ?? '' }
                        : undefined,
                    )
                  }
                  pagingEnabled
                  placeholder=""
                  disabled={disabled}
                  container={calendarContainer}
                />
              )}
            />
          ),
        });

      return (
        <form
          onSubmit={handleSubmit(isBulkMode ? bulkSubmit : (onSubmit as any))}
          className="flex flex-col gap-6"
        >
          {semen && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-2 text-sm font-semibold text-gray-700">
                {t('semen.selectedSemen')}
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-600">
                    {t('common.name')}:
                  </span>{' '}
                  <span className="text-gray-900">
                    {'name' in semen ? semen.name : ''}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">
                    {t('semen.naab')}:
                  </span>{' '}
                  <span className="text-gray-900">
                    {'naab' in semen ? semen.naab : ''}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">
                    {t('semen.cane')}:
                  </span>{' '}
                  <span className="text-gray-900">
                    {'cane' in semen ? semen.cane : ''}
                  </span>
                </div>
                {semen.sire && (
                  <div>
                    <span className="font-medium text-gray-600">
                      {t('semen.sire')}:
                    </span>{' '}
                    <span className="text-gray-900">
                      {semen.sire.name || ''}
                      {semen.sire.registrationNumber
                        ? ` (${semen.sire.registrationNumber})`
                        : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-3">
            {renderField({
              name: 'date',
              label: t('common.date'),
              required: true,
              children: (
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DatePickerInput
                      id="date"
                      className={DATE_PICKER_COMPACT}
                      initialDate={field.value}
                      container={calendarContainer}
                      onChange={(date) => field.onChange(date ?? undefined)}
                    />
                  )}
                />
              ),
            })}
            {renderField({
              name: 'transactionId',
              label: t('semenTransaction.transactionId'),
              required: true,
              children: (
                <Controller
                  name="transactionId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || null}
                      onChange={(value) => field.onChange(value ?? '')}
                      items={transactionOptions}
                    >
                      <SelectTrigger
                        id="transactionId"
                        className="w-full"
                        aria-invalid={!!errors.transactionId}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent
                        alignItemWithTrigger={false}
                        container={calendarContainer}
                      >
                        {transactionOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              ),
            })}
            {renderField({
              name: 'quantity',
              label: t('semenTransaction.quantity'),
              required: !isBulkMode,
              children: isBulkMode ? (
                <p className="text-muted-foreground text-xs italic">
                  {t(
                    'semenTransaction.quantityFromGrid',
                    'Quantity is taken from each semen\'s "Number of Straws to Add" in the grid.',
                  )}
                </p>
              ) : (
                <Controller
                  name="quantity"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="quantity"
                      type="number"
                      value={field.value?.toString() || ''}
                      aria-invalid={!!errors.quantity}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? parseInt(e.target.value) : undefined,
                        )
                      }
                    />
                  )}
                />
              ),
            })}
            {renderField({
              name: 'description',
              label: t('common.description'),
              required: true,
              children: (
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="description"
                      value={field.value || ''}
                      aria-invalid={!!errors.description}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              ),
            })}
            {renderAccountField(
              'newOwner',
              t('semenTransaction.newOwner'),
              !isTransfer,
            )}
            {renderLocationField(
              'newLocation',
              t('semenTransaction.newLocation'),
              !isTransfer,
            )}
            {renderAccountField(
              'previousOwner',
              t('semenTransaction.previousOwner'),
              true,
            )}
            {renderField({
              name: 'quantityBeforeTransaction',
              label: t('semenTransaction.quantityBeforeTransaction'),
              children: (
                <Controller
                  name="quantityBeforeTransaction"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="quantityBeforeTransaction"
                      type="number"
                      value={field.value?.toString() || ''}
                      disabled
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? parseInt(e.target.value) : undefined,
                        )
                      }
                    />
                  )}
                />
              ),
            })}
            {renderLocationField(
              'previousLocation',
              t('semenTransaction.previousLocation'),
              true,
            )}
          </div>
          <div className="flex justify-end gap-2">
            <UiButton
              variant="outline"
              type="button"
              onClick={() => {
                reset();
                onCancel?.();
              }}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </UiButton>
            <UiButton type="submit" disabled={isSubmitting}>
              {t('common.save')}
            </UiButton>
          </div>
        </form>
      );
    }

    return (
      <form
        onSubmit={handleSubmit(isBulkMode ? bulkSubmit : (onSubmit as any))}
        className="bg-white p-6"
      >
        {semen && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-2 text-sm font-semibold text-gray-700">
              Selected Semen
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-600">Name:</span>{' '}
                <span className="text-gray-900">
                  {'name' in semen ? semen.name : ''}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">NAAB:</span>{' '}
                <span className="text-gray-900">
                  {'naab' in semen ? semen.naab : ''}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Cane:</span>{' '}
                <span className="text-gray-900">
                  {'cane' in semen ? semen.cane : ''}
                </span>
              </div>
              {semen.sire && (
                <div>
                  <span className="font-medium text-gray-600">Sire:</span>{' '}
                  <span className="text-gray-900">
                    {semen.sire.name || ''}
                    {semen.sire.registrationNumber
                      ? ` (${semen.sire.registrationNumber})`
                      : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="mb-8">
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-1">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label
                    id="date-label"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t('common.date')}
                  </label>
                  <span className="text-red-500">*</span>
                </div>

                <div className="w-48">
                  <Controller
                    name="date"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Calendar
                        id="date"
                        aria-labelledby="date-label"
                        value={field.value}
                        className={classNames('w-full', {
                          'p-invalid': fieldState.invalid,
                        })}
                        onChange={(e) => field.onChange(e.value)}
                        showIcon
                        dateFormat="mm/dd/yy"
                        showOnFocus={false}
                        placeholder="10/7/2025"
                        container={calendarContainer}
                      />
                    )}
                  />
                  {getFormErrorMessage('date')}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="transactionId"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t('semenTransaction.transactionId')}
                  </label>
                  <span className="text-red-500">*</span>
                </div>
                <div className="w-48">
                  <Controller
                    name="transactionId"
                    control={control}
                    render={({ field, fieldState }) => (
                      <OptionSetsDropdown
                        id="transactionId"
                        value={field.value}
                        optionSetsName="Semen Transaction"
                        filterOptions={(item) => {
                          const allowedLabels = [
                            'Shipment',
                            'Transfer',
                            'Adjustment',
                          ];
                          return allowedLabels.includes(item.label || '');
                        }}
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        onChange={(e) => field.onChange(e.value)}
                      />
                    )}
                  />
                  {getFormErrorMessage('transactionId')}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="quantity"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t('semenTransaction.quantity')}
                  </label>
                  {!isBulkMode && <span className="text-red-500">*</span>}
                </div>
                <div className="w-48">
                  {isBulkMode ? (
                    <p className="text-xs text-gray-500 italic">
                      {t(
                        'semenTransaction.quantityFromGrid',
                        'Quantity is taken from each semen\'s "Number of Straws to Add" in the grid.',
                      )}
                    </p>
                  ) : (
                    <>
                      <Controller
                        name="quantity"
                        control={control}
                        render={({ field, fieldState }) => (
                          <InputText
                            id="quantity"
                            value={field.value?.toString() || ''}
                            className={classNames('w-full text-sm', {
                              'p-invalid': fieldState.invalid,
                            })}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value
                                  ? parseInt(e.target.value)
                                  : undefined,
                              )
                            }
                            type="number"
                          />
                        )}
                      />
                      {getFormErrorMessage('quantity')}
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="description"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t('common.description')}
                  </label>
                  <span className="text-red-500">*</span>
                </div>
                <div className="w-48">
                  <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState }) => (
                      <InputText
                        id="description"
                        value={field.value || ''}
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    )}
                  />
                  {getFormErrorMessage('description')}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t('semenTransaction.newOwner')}
                  </label>
                </div>
                <div className="w-48">
                  <Controller
                    name="newOwner"
                    control={control}
                    render={({ field, fieldState }) => (
                      <AccountAutoComplete
                        id={field.name}
                        key={field.value?.id || 'newOwner'}
                        defaultValue={field.value as any}
                        value={field.value as any}
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        onSelect={(e: AutoCompleteSelectEvent) => {
                          field.onChange(e.value);
                        }}
                        disabled={transactionLabel !== 'Transfer'}
                      />
                    )}
                  />
                  {getFormErrorMessage('newOwner')}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t('semenTransaction.newLocation')}
                  </label>
                </div>
                <div className="w-48">
                  <Controller
                    name="newLocation"
                    control={control}
                    render={({ field, fieldState }) => (
                      <StorageLocationAutoComplete
                        defaultValue={field.value as any}
                        value={field.value as any}
                        field="name"
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        onSelect={(e: AutoCompleteSelectEvent) =>
                          field.onChange(e.value)
                        }
                        disabled={transactionLabel !== 'Transfer'}
                      />
                    )}
                  />
                  {getFormErrorMessage('newLocation')}
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t('semenTransaction.previousOwner')}
                  </label>
                </div>
                <div className="w-48">
                  <Controller
                    name="previousOwner"
                    control={control}
                    render={({ field, fieldState }) => (
                      <AccountAutoComplete
                        id={field.name}
                        key={field.value?.id || 'previousOwner'}
                        defaultValue={
                          field.value
                            ? ({
                                id: field.value.id,
                                name: field.value.name,
                              } as any)
                            : undefined
                        }
                        defaultId={
                          field.value?.id && !field.value.name
                            ? field.value.id
                            : undefined
                        }
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        onSelect={(e: AutoCompleteSelectEvent) => {
                          field.onChange(e.value);
                        }}
                        disabled
                      />
                    )}
                  />
                  {getFormErrorMessage('previousOwner')}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="quantityBeforeTransaction"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t('semenTransaction.quantityBeforeTransaction')}
                  </label>
                </div>
                <div className="w-48">
                  <Controller
                    name="quantityBeforeTransaction"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        id="quantityBeforeTransaction"
                        value={field.value?.toString() || ''}
                        className="w-full text-sm"
                        onChange={(e) =>
                          field.onChange(
                            e.target.value
                              ? parseInt(e.target.value)
                              : undefined,
                          )
                        }
                        type="number"
                        disabled
                      />
                    )}
                  />
                  {getFormErrorMessage('quantityBeforeTransaction')}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t('semenTransaction.previousLocation')}
                  </label>
                </div>
                <div className="w-48">
                  <Controller
                    name="previousLocation"
                    control={control}
                    render={({ field, fieldState }) => (
                      <StorageLocationAutoComplete
                        key={field.value?.id || 'previousLocation'}
                        defaultValue={field.value as any}
                        field="name"
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        onSelect={(e: AutoCompleteSelectEvent) =>
                          field.onChange(e.value)
                        }
                        disabled
                      />
                    )}
                  />
                  {getFormErrorMessage('previousLocation')}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-6">
          <Button
            type="button"
            label={t('common.cancel')}
            icon="pi pi-times"
            severity="secondary"
            onClick={() => {
              reset();
              onCancel?.();
            }}
            disabled={isSubmitting}
          />
          <Button
            type="submit"
            label={t('common.save')}
            icon="pi pi-check"
            loading={isSubmitting}
          />
        </div>
      </form>
    );
  },
);
